import { DataTable, DataTablePagination } from 'components/common'
import { columns } from './utils'
import { useQuery } from 'hooks'
import { RowClickedEvent } from '@ag-grid-community/core'
import { useLocation, useNavigate } from 'react-router-dom'
import { PAGE_INVENTORY_UPDATE } from 'utils/constants'
import { memo, useEffect, useState } from 'react'
import { fetchInventoryDetails } from 'utils/api/inventoryApi'
import { SetSelectedColumns } from 'types/dataTable'
import toast from 'react-hot-toast'
import {
  BARCODE_COUNT_KEY,
  BARCODE_ERROR_MESSAGES,
  BARCODE_QUERY_KEY,
  BARCODE_SEARCH_EXPIRED,
  BARCODE_TOKEN_KEY,
  getBarcodeErrorCode,
} from 'components/common/barcodeSearch/constants'

type Props = {
  isEditor: boolean
  setSelectedColumns: SetSelectedColumns
  loading: boolean
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
}

const InventoryDataTable = ({
  loading,
  setLoading,
  isEditor,
  setSelectedColumns,
}: Props) => {
  const [inventoryResponse, setInventoryUpdate] = useState<any>({})

  const navigate = useNavigate()
  const { search } = useLocation()
  const { getAllQueries, setQuery, setMultipleQueries } = useQuery()

  const getInventory = async () => {
    setLoading(true)
    const queries = getAllQueries()
    const { limit, page, search, inventoryFilters, barcodeToken } = queries
    const barcode = queries[BARCODE_QUERY_KEY]
    try {
      const { data } = await fetchInventoryDetails({
        limit: limit || 10,
        page: page || 1,
        search: search,
        filters: inventoryFilters,
        barcodeToken: barcodeToken,
        barcode: barcode,
      })
      setSelectedColumns(data?.tableConfig?.inventory || [])
      setInventoryUpdate({ ...data })
    } catch (error) {
      // Barcode search token expired → clear it (triggers a normal refetch)
      // and prompt the user to upload the file again.
      if (getBarcodeErrorCode(error) === BARCODE_SEARCH_EXPIRED) {
        setMultipleQueries({
          [BARCODE_TOKEN_KEY]: undefined,
          [BARCODE_COUNT_KEY]: undefined,
          page: 1,
        })
        toast.error(BARCODE_ERROR_MESSAGES[BARCODE_SEARCH_EXPIRED], {
          duration: 4000,
        })
      }
      setInventoryUpdate({ docs: [] })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getInventory()
  }, [search])

  useEffect(() => {
    const columnUrlParams = localStorage.getItem('inventoryColumnSelected')
    if (columnUrlParams) {
      setQuery('cols', JSON.parse(columnUrlParams))
    }
  }, [])

  const onRowClicked = (e: RowClickedEvent) => {
    if (!isEditor) return
    navigate(`${PAGE_INVENTORY_UPDATE.path}/${e.data._id}`)
  }

  // Inventory list totals are NOT filter-aware (totalDocs/totalPages count the
  // whole collection), so during a barcode search the backend's
  // hasNextPage/totalPages can't be trusted. Drive paging from the page
  // contents instead: a full page implies there may be another one.
  const activeQueries = getAllQueries()
  const barcodeActive = !!(
    activeQueries[BARCODE_TOKEN_KEY] || activeQueries[BARCODE_QUERY_KEY]
  )
  const rows = inventoryResponse?.docs ?? []
  const currentPage = Number(inventoryResponse?.page ?? 1)
  const pageLimit = Number(inventoryResponse?.limit ?? 10)

  const hasNextPage = barcodeActive
    ? rows.length >= pageLimit
    : inventoryResponse?.hasNextPage
  const hasPrevPage = barcodeActive
    ? currentPage > 1
    : inventoryResponse?.hasPrevPage
  const nextPage = barcodeActive ? currentPage + 1 : inventoryResponse?.nextPage
  const prevPage = barcodeActive ? currentPage - 1 : inventoryResponse?.prevPage
  const totalPages = barcodeActive
    ? currentPage + (hasNextPage ? 1 : 0)
    : inventoryResponse?.totalPages

  return (
    <>
      <DataTable
        columns={loading ? [] : columns}
        rows={loading ? null : inventoryResponse?.docs}
        onRowClicked={isEditor ? onRowClicked : () => {}}
        identifier="_id"
        activeColumnsKey="inventory"
        activeColumns={inventoryResponse?.tableConfig?.inventory || []}
      />

      <DataTablePagination
        currentPage={inventoryResponse?.page}
        totalPages={totalPages}
        hasNextPage={hasNextPage}
        hasPrevPage={hasPrevPage}
        limit={inventoryResponse?.limit}
        nextPage={nextPage}
        prevPage={prevPage}
        disabled={loading}
        disableGoToLastPage
        shouldDisplayTotalPages={false}
      />
    </>
  )
}

export default memo(InventoryDataTable)
